import {
  Identifiable,
  MenuItemOption,
} from "@components/common/mobile/mobile-types";
import { SphereDivider } from "@components/common/sphere-divider";
import { IconButton, Menu, MenuItem, neutral, ThreeDotsIcon } from "@flat-ui";
import { Box } from "@mui/material";
import { useRef, useState } from "react";

interface Props<T extends Identifiable> {
  /** The item being operated on */
  item: T;
  /** Array of options with label, icon and click handler */
  menuItems: MenuItemOption<T>[];
}

/**
 * Renders a three-dots menu button for list items and handles its open/close state.
 * Expects a list of menu items with label, icon and click handler; renders nothing else.
 *
 * @template T - The type of item being operated on
 */
export function ListItemMenu<T extends Identifiable>({
  item,
  menuItems,
}: Props<T>): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  /** Resolves whether a divider should be shown after a menu item */
  function shouldShowDivider(menuItem: MenuItemOption<T>): boolean {
    return typeof menuItem.shouldShowDividerAfter === "function"
      ? menuItem.shouldShowDividerAfter(item)
      : !!menuItem.shouldShowDividerAfter;
  }

  const visibleMenuItems = menuItems.filter((menuItem) =>
    menuItem.shouldShow ? menuItem.shouldShow(item) : true
  );

  return (
    <Box
      data-testid="three-dot-menu"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <IconButton
        ref={buttonRef}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();
          setIsOpen(true);
        }}
        size="s"
      >
        <ThreeDotsIcon />
      </IconButton>

      <Menu
        open={isOpen}
        anchorEl={buttonRef.current}
        onClose={() => setIsOpen(false)}
      >
        {visibleMenuItems.map((menuItem, idx) => {
          let content: JSX.Element;

          if (menuItem.component) {
            content =
              typeof menuItem.component === "function"
                ? menuItem.component(item, () => setIsOpen(false))
                : menuItem.component;
          } else {
            const hrefUrl = menuItem.getHrefUrl?.(item);

            content = (
              // Use capture phase to intercept left-clicks before FaroMenuItem's internal
              // navigate(href), which misroutes external URLs as relative paths.
              // Middle-click and right-click are unaffected since they don't fire "click".
              <Box
                onClickCapture={
                  menuItem.shouldHandleNavigation
                    ? (e: React.MouseEvent) => {
                        e.stopPropagation();
                        e.preventDefault();
                        menuItem.onSelect?.(item);
                        setIsOpen(false);
                      }
                    : undefined
                }
              >
                <MenuItem
                  label={menuItem.label}
                  href={hrefUrl}
                  onClick={() => {
                    menuItem.onSelect?.(item);
                    setIsOpen(false);
                  }}
                  Icon={menuItem.Icon}
                  sx={menuItem.menuItemSx}
                />
              </Box>
            );
          }

          return (
            <Box key={idx}>
              {content}
              {shouldShowDivider(menuItem) && (
                <Box sx={{ margin: 0 }}>
                  <SphereDivider sx={{ borderColor: neutral[200] }} />
                </Box>
              )}
            </Box>
          );
        })}
      </Menu>
    </Box>
  );
}
